import { TrialProcessOptions } from '@/services/pdf-service'
import { Track } from '@/services/track'
import {
  ButtonLink,
  ColorInput,
  Control,
  Icon,
  FieldLabelLayout,
  NullableTextInput,
  ObjectController,
  ScrollablePanel,
  TextInput,
  WithBeatUIBreakpoint,
} from '@tempots/beatui'
import { attr, Fragment, html, on, prop, When } from '@tempots/dom'
import { PhoneNumberInput } from './phone-number-input'
import { LooseEmailInput } from './loose-email-input'
import { LinkedInInput } from './linkedin-input'
import { LooseUrlInput } from './loose-url-input'
import { Highlight } from './highlight'

export interface TrialInputsOptions {
  controller: ObjectController<TrialProcessOptions>
}

// Beatui's horizontal-fixed label column defaults to 7.5rem, which the
// longest labels here ("Agency phone number") overflow.
const LABEL_WIDTH = '11.5rem'

export function TrialInputs({ controller }: TrialInputsOptions) {
  return WithBeatUIBreakpoint(info => {
    // The expanded pitch panel lives in the ScrollablePanel's NON-scrolling
    // footer: expanded, it squeezes the form body to a sliver on smaller
    // screens. It starts expanded only when the window is tall enough to
    // fit the form AND the pitch comfortably; below that it starts
    // collapsed to a compact banner that keeps the headline and — always —
    // the sign-up CTA. Both states are toggleable.
    const showSignupPanel = prop(window.innerHeight >= 960)
    const layout = info.value.map(
      (v): FieldLabelLayout =>
        info.is('<md', v.width) ? 'vertical' : 'horizontal-fixed'
    )
    // The primary CTA: present in the expanded pitch AND the collapsed
    // banner — collapsing the pitch must never hide the sign-up path.
    const signupCta = () =>
      ButtonLink(
        { href: '/signup', color: 'primary', size: 'lg' },
        on.click(() =>
          Track.click({
            section: 'main',
            element_type: 'button',
            click_action: 'navigate',
            element_id: 'signup',
          })
        ),
        attr.class('w-full'),
        'Sign up and save your information'
      )
    return ScrollablePanel({
      header: html.div(
        attr.class('px-2 md:px-4 py-1 md:py-2'),
        html.h1(
          attr.class('text-lg md:text-xl font-bold text-gray-900'),
          'Try ResumeHeaders'
        )
      ),
      body: html.div(
        attr.class(
          'p-2 md:p-4 pt-0 md:pt-0 flex flex-col gap-2 overflow-y-auto'
        ),
        html.h2(
          attr.class('text-lg font-semibold text-gray-900'),
          'Customize recruiter information:'
        ),
        Control(TextInput, {
          triggerOn: 'input',
          layout,
          labelWidth: LABEL_WIDTH,
          controller: controller.field('headerRecruiterName'),
          label: 'Your name',
          placeholder: 'Your Name',
          onChange: v =>
            Track.fieldInput({
              section: 'main',
              field_name: 'headerRecruiterName',
              previous_value:
                controller.field('headerRecruiterName').signal.value ?? '',
              new_value: v,
            }),
        }),
        Control(PhoneNumberInput, {
          triggerOn: 'input',
          layout,
          labelWidth: LABEL_WIDTH,
          controller: controller.field('headerRecruiterPhoneNumber'),
          label: 'Your phone number',
          placeholder: '(444) 444-4444',
          onChange: v => {
            Track.fieldInput({
              section: 'main',
              field_name: 'headerRecruiterPhoneNumber',
              previous_value:
                controller.field('headerRecruiterPhoneNumber').signal.value ??
                '',
              new_value: v ?? '',
            })
          },
        }),
        Control(LooseEmailInput, {
          triggerOn: 'input',
          layout,
          labelWidth: LABEL_WIDTH,
          controller: controller.field('headerRecruiterEmail'),
          label: 'Your email',
          onChange: v =>
            Track.fieldInput({
              section: 'main',
              field_name: 'headerRecruiterEmail',
              previous_value:
                controller.field('headerRecruiterEmail').signal.value ?? '',
              new_value: v ?? '',
            }),
        }),
        Control(LinkedInInput, {
          triggerOn: 'input',
          layout,
          labelWidth: LABEL_WIDTH,
          controller: controller.field('headerRecruiterLinkedIn'),
          label: 'Your LinkedIn',
          onChange: v =>
            Track.fieldInput({
              section: 'main',
              field_name: 'headerRecruiterLinkedIn',
              previous_value:
                controller.field('headerRecruiterLinkedIn').signal.value ?? '',
              new_value: v ?? '',
            }),
        }),
        html.h2(
          attr.class('text-lg font-semibold text-gray-900 mt-3 md:mt-5'),
          'Customize agency information:'
        ),
        Control(NullableTextInput, {
          triggerOn: 'input',
          layout,
          labelWidth: LABEL_WIDTH,
          controller: controller.field('headerAgencyName'),
          label: 'Agency name',
          placeholder: 'Example Agency',
          onChange: v =>
            Track.fieldInput({
              section: 'main',
              field_name: 'headerAgencyName',
              previous_value:
                controller.field('headerAgencyName').signal.value ?? '',
              new_value: v ?? '',
            }),
        }),
        Control(PhoneNumberInput, {
          triggerOn: 'input',
          layout,
          labelWidth: LABEL_WIDTH,
          controller: controller.field('headerAgencyPhoneNumber'),
          label: 'Agency phone number',
          onChange: v =>
            Track.fieldInput({
              section: 'main',
              field_name: 'headerAgencyPhoneNumber',
              previous_value:
                controller.field('headerAgencyPhoneNumber').signal.value ?? '',
              new_value: v ?? '',
            }),
        }),
        Control(LooseUrlInput, {
          triggerOn: 'input',
          layout,
          labelWidth: LABEL_WIDTH,
          controller: controller.field('headerAgencyUrl'),
          label: 'Agency website',
          onChange: v =>
            Track.fieldInput({
              section: 'main',
              field_name: 'headerAgencyUrl',
              previous_value:
                controller.field('headerAgencyUrl').signal.value ?? '',
              new_value: v ?? '',
            }),
        }),
        Control(LinkedInInput, {
          triggerOn: 'input',
          layout,
          labelWidth: LABEL_WIDTH,
          controller: controller.field('headerAgencyLinkedIn'),
          label: 'Agency LinkedIn',
          onChange: v =>
            Track.fieldInput({
              section: 'main',
              field_name: 'headerAgencyLinkedIn',
              previous_value:
                controller.field('headerAgencyLinkedIn').signal.value ?? '',
              new_value: v ?? '',
            }),
        }),
        Control(LooseEmailInput, {
          triggerOn: 'input',
          layout,
          labelWidth: LABEL_WIDTH,
          controller: controller.field('headerAgencyEmail'),
          label: 'Agency email',
          placeholder: 'info@example.com',
          onChange: v =>
            Track.fieldInput({
              section: 'main',
              field_name: 'headerAgencyEmail',
              previous_value:
                controller.field('headerAgencyEmail').signal.value ?? '',
              new_value: v ?? '',
            }),
        }),
        // Set apart from the agency contact fields: the color shapes the
        // whole design, not just one header line.
        html.div(attr.class('mt-2 md:mt-4')),
        Control(ColorInput, {
          triggerOn: 'input',
          layout,
          labelWidth: LABEL_WIDTH,
          controller: controller.field('agencyBrandColor'),
          label: 'Brand color',
          placeholder: '#000000',
          fullWidth: false,
          onChange: v =>
            Track.fieldInput({
              section: 'main',
              field_name: 'agencyBrandColor',
              previous_value:
                controller.field('agencyBrandColor').signal.value ?? '',
              new_value: v,
            }),
        })
      ),
      footer: Fragment(
        attr.class('p-2'),
        When(
          showSignupPanel,
          () =>
            html.div(
              attr.class(
                'p-2 md:p-4 lg:p-6 bg-teal-50 rounded-lg border border-teal-200 relative text-xs lg:text-sm'
              ),
              // Dismissable at every size — a laptop user whose window is
              // taller than the threshold may still want the form space.
              html.button(
                attr.class(
                  'absolute top-2 right-2 text-gray-500 hover:text-gray-700 transition-colors'
                ),
                attr.type('button'),
                on.click(() => {
                  Track.click({
                    section: 'main',
                    element_type: 'button',
                    click_action: 'toggle',
                    element_id: 'signup',
                  })
                  showSignupPanel.set(false)
                }),
                Icon({ icon: 'fe:close' })
              ),
              html.h3(
                attr.class(
                  'text-base lg:text-lg font-semibold text-teal-900 mb-1 lg:mb-4'
                ),
                'Do more with ',
                Highlight('ResumeHeaders')
              ),
              html.ul(
                attr.class('text-teal-800 mb-2 space-y-0 md:space-y-1'),
                html.li(
                  Icon({ icon: 'fe:check', color: 'teal' }),
                  ' Create ',
                  html.b('any design'),
                  ' — not just this layout:'
                ),
                html.li(
                  attr.class('pl-6'),
                  '– upload a branded resume and copy the header you already use'
                ),
                html.li(
                  attr.class('pl-6'),
                  '– or build your own header & footer from scratch, no constraints'
                ),
                html.li(
                  Icon({ icon: 'fe:check', color: 'teal' }),
                  ' More options including footer, logo and others'
                ),
                html.li(
                  Icon({ icon: 'fe:check', color: 'teal' }),
                  ' Save your information'
                ),
                html.li(
                  Icon({ icon: 'fe:check', color: 'teal' }),
                  ' Add a cover page with a candidate write-up'
                ),
                html.li(
                  Icon({ icon: 'fe:check', color: 'teal' }),
                  ' Add a watermark'
                ),
                html.li(
                  Icon({ icon: 'fe:check', color: 'teal' }),
                  ' Redact candidate information'
                ),
                html.li(
                  Icon({ icon: 'fe:check', color: 'teal' }),
                  ' Brand more resumes'
                )
              ),
              html.div(
                attr.class('text-sm text-gray-600 mb-1 md:mb-4'),
                'No credit card required. Cancel anytime.'
              ),
              signupCta()
            ),
          // Collapsed: a compact banner that keeps the headline (with an
          // expand affordance) and the primary sign-up CTA.
          () =>
            html.div(
              attr.class(
                'p-2 md:p-3 bg-teal-50 rounded-lg border border-teal-200 flex flex-col gap-2 text-xs lg:text-sm'
              ),
              html.button(
                attr.type('button'),
                attr.class(
                  'flex flex-row items-center justify-between w-full text-left'
                ),
                on.click(() => {
                  Track.click({
                    section: 'main',
                    element_type: 'button',
                    click_action: 'toggle',
                    element_id: 'signup',
                  })
                  showSignupPanel.set(true)
                }),
                html.h3(
                  attr.class('text-base font-semibold text-teal-900'),
                  'Do more with ',
                  Highlight('ResumeHeaders')
                ),
                html.span(
                  attr.class(
                    'flex flex-row items-center gap-1 text-teal-700 hover:text-teal-900 transition-colors'
                  ),
                  'Why sign-up?',
                  Icon({ icon: 'fe:arrow-up', color: 'teal' })
                )
              ),
              signupCta()
            )
        )
      ),
    })
  })
}
